import { CommonModule } from '@angular/common';
import { Component, input, TemplateRef } from '@angular/core';

export interface TableColumn<T> {
  key: keyof T & string;
  label: string;
  sortable?: boolean;
  sortFn?: (a: T, b: T) => number;
  template?: TemplateRef<any>;
}

type PaginationItem = {
  display: number | '...';
  isCurrent: boolean;
  jumpTo?: number;
};

@Component({
  selector: 'app-table',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
})
export class TableComponent<T> {
  columns = input.required<TableColumn<T>[]>();
  rowsData = input.required<T[]>();
  pageSize = input<number | undefined>(undefined);

  currentPage: number = 1;
  sortedData: T[] = [];
  pagedData: T[] = [];
  currentSort: { key: string; direction: 'asc' | 'desc' | null } | null = null;

  ngOnChanges(): void {
    this.sortedData = [...this.rowsData()];
    this.updatePagedData();
  }

  sort(column: TableColumn<T>): void {
    if (!column.sortable || !column.sortFn) return;

    let nextDirection: 'asc' | 'desc' | null = 'asc';

    if (this.currentSort?.key === column.key) {
      if (this.currentSort.direction === 'asc') nextDirection = 'desc';
      else if (this.currentSort.direction === 'desc') nextDirection = null;
    }

    this.currentSort = nextDirection
      ? { key: column.key, direction: nextDirection }
      : null;

    if (this.currentSort) {
      this.sortedData.sort((a, b) => {
        const result = column.sortFn!(a, b);
        return this.currentSort!.direction === 'asc' ? result : -result;
      });
    } else {
      this.sortedData = [...this.rowsData()]; // reset sort
    }

    this.currentPage = 1;
    this.updatePagedData();
  }

  updatePagedData(): void {
    const pageSize = this.pageSize();
    if (!!pageSize) {
      const start = (this.currentPage - 1) * pageSize;
      this.pagedData = this.sortedData.slice(start, start + pageSize);
    } else {
      this.pagedData = this.sortedData.slice();
    }
  }

  changePage(delta: number): void {
    const totalPages = this.totalPages;
    this.currentPage = Math.min(
      Math.max(this.currentPage + delta, 1),
      totalPages
    );
    this.updatePagedData();
  }

  isSortedAsc(col: TableColumn<T>): boolean {
    return (
      this.currentSort?.key === col.key && this.currentSort.direction === 'asc'
    );
  }

  isSortedDesc(col: TableColumn<T>): boolean {
    return (
      this.currentSort?.key === col.key && this.currentSort.direction === 'desc'
    );
  }

  get totalPages(): number {
    return Math.ceil(
      this.sortedData.length / (this.pageSize() ?? this.sortedData.length)
    );
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.updatePagedData();
  }

  getPaginationItems(): PaginationItem[] {
    const items: PaginationItem[] = [];
    const total = this.totalPages;
    const currentPage = this.currentPage;

    if (total <= 7) {
      for (let i = 1; i <= total; i++) {
        items.push({ display: i, isCurrent: i === currentPage });
      }
    } else {
      if (currentPage > 2) {
        items.push({ display: 1, isCurrent: currentPage === 1 });

        if (currentPage > 3) {
          items.push({
            display: '...',
            isCurrent: false,
            jumpTo: currentPage - 2,
          });
        }
      }

      const start = Math.max(1, currentPage - 1);
      const end = Math.min(total, currentPage + 1);
      for (let i = start; i <= end; i++) {
        if (!items.some((it) => it.display === i)) {
          items.push({ display: i, isCurrent: i === currentPage });
        }
      }

      if (currentPage < total - 1) {
        if (currentPage < total - 2) {
          items.push({
            display: '...',
            isCurrent: false,
            jumpTo: currentPage + 2,
          });
        }
        items.push({ display: total, isCurrent: currentPage === total });
      }
    }

    return items;
  }
}
